import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import Loader from "Components/UI/Loader";
import { getLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import MaterialSelect from "@mui/material/Select";
import { MenuItem } from "@mui/material";
import {
  createCommonUtil,
  getFilteredCommonUtils,
  getUtilsTypes,
  updateCommonUtil,
} from "Network/Core/CommonUtils/commonUtils";
import { CommonUtil } from "Network/Core/CommonUtils/types";
import CountrySelect from "Components/UI/ContrySelect";
import { handleError } from "Library/Utils";

// Images
import images from "Assets/images";

// CSS
import "Components/Common/Modal/style.css";
import "Components/Core/BookingFormModule/new-booking-form.css";

// Hooks
import { useApiCall } from "Hooks/useApiCall";
import { useTranslationCheck } from "Hooks/useTranslationCheck";

export interface CommonUtilModalProps {
  handleClose?: () => void;
  existingData: CommonUtil;
}

const CommonUtilModal = ({
  handleClose = () => {},
  existingData,
}: CommonUtilModalProps) => {
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const { t, tCheck } = useTranslationCheck();
  const { call } = useApiCall();

  const [commonUtil, setCommonUtil] = useState<CommonUtil>({
    name: "",
    type: "",
    conditions: "",
    parent:""
  });

  const [commonUtilTypeOptions, setCommonUtilTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [activityTypeOptions, setActivityTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  const handleCountryChange = (value: string) => {
    setCommonUtil((prev) => ({
      ...prev,
      name: value,
    }));
  };

 const handleChange = (e: { target: { name: string; value: any } }) => {
    const { name, value } = e.target;

    if (!name) return;

    if (name === "type") {
      setCommonUtil((prev) => ({
        ...prev,
        [name]: value as string,
        conditions: "", // clear conditions when type changes
      }));
      if (value === "EQUIPMENT_USAGE_TYPE") {
        fetchActivityTypes();
      }
    } else if (name === "parent") {
      setCommonUtil((prev) => ({
        ...prev,
        parent: value as string,
      }));
    } else {
      setCommonUtil((prev) => ({
        ...prev,
        [name]: value as string,
      }));
    }
  };

  const fetchUtilTypes = async () => {
    call(() => getUtilsTypes(region), (response) => {
      const sortedOptions = response
        .map((item: string) => ({
          label: item,
          value: item,
        }))
        .sort((a:any, b:any) => a.label.localeCompare(b.label));
      setCommonUtilTypeOptions(sortedOptions);
    });
  };

  const fetchActivityTypes = async () => {
    call(() =>  getFilteredCommonUtils("MAIN_ACTIVITY",region), (response) => {
      const sortedOptions = response
        .map((item: any) => ({
          label: item.name,
          value: item.id,
        }))
        .sort((a:any, b:any) => a.label.localeCompare(b.label));
      setActivityTypeOptions(sortedOptions);
    });
  };

  useEffect(() => {
    if (existingData?.id) {
      setCommonUtil(existingData);
      if (existingData.type === "EQUIPMENT_USAGE_TYPE") {
        fetchActivityTypes();
      }
    }
    fetchUtilTypes();
  }, [existingData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const apiCall = existingData?.id
      ? updateCommonUtil(existingData.id, commonUtil, region)
      : createCommonUtil(commonUtil, region);

    call(
      () => apiCall,
      () => {
        setIsLoading(false);
        toast.success(t("Translation.saved_details_success_fully"));
        handleClose();
      },
      (error) => {
        setIsLoading(false);
        handleError(error, t);
      }
    );
  };

  return (
    <>
      {isLoading && <Loader />}
      <div
        className="modal fade show vc-user-invite"
        id="invite_user_Modal"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div className="invite-user-header">
                <h2>
                  {t(
                    existingData?.id
                      ? "Translation.commonUtil_master_edit"
                      : "Translation.commonUtil_master_add"
                  )}
                </h2>
              </div>
              <div className="invite-user-header vc-invite-from-procore-link">
                <Link onClick={handleClose} to="">
                  {t("Translation.close")}
                  <img src={images.modalCloseIcon} alt="Close" />
                </Link>
              </div>
            </div>

            <div className="modal-body">
              <div className="custom-form">
                <form onSubmit={handleSubmit}>

                  {/* Type Field */}
                  <div className="row mb-3 align-items-center">
                    <div className="col-sm-4">
                      <label className="form-label mb-0">{t("Translation.type")}</label>
                    </div>
                    <div className="col-sm-8">
                      <MaterialSelect
                        style={{ width: "22rem" }}
                        inputProps={{
                          id: "type",
                          name: "type",
                          required: true,
                          className: "form-control",
                        }}
                        value={commonUtil.type}
                        onChange={handleChange}
                      >
                        {commonUtilTypeOptions.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </MaterialSelect>
                    </div>
                  </div>
                  
                  {/* Name Field */}
                  <div className="row mb-3 align-items-center">
                    <div className="col-sm-4">
                      <label htmlFor="commonUtilName" className="form-label mb-0">
                        {t("Translation.name")}
                      </label>
                    </div>
                    <div className="col-sm-8">
                      {commonUtil.type === "COUNTRY" ? (
                        <CountrySelect 
                          value={commonUtil.name}
                          onChange={handleCountryChange}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          id="commonUtilName"
                          name="name"
                          value={commonUtil.name}
                          onChange={handleChange}
                          required
                        />
                      )}
                    </div>
                  </div>

                  {/* Conditional Fields */}
                  {commonUtil.type === "EQUIPMENT_USAGE_TYPE" && (
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <label className="form-label mb-0">{t("Translation.activity_type")}</label>
                      </div>
                      <div className="col-sm-8">
                        <MaterialSelect
                          style={{ width: "22rem" }}
                          inputProps={{
                            id: "parent",
                            name: "parent",
                            required: true,
                            className: "form-control",
                          }}
                          value={commonUtil.parent || ""}
                          onChange={handleChange}
                        >
                          {activityTypeOptions.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {tCheck(item.label)}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      </div>
                    </div>
                  )}

                  <div className="modal-footer">
                    <button type="submit" className="btn theme-btn">
                      {t("Translation.submit")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommonUtilModal;
